import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MeterMediaType } from '../../../consumption/model/const/enums';
import { Meter } from '../../../consumption/model/meter';
import { RentalObjectConsumptionMeter } from '../../../consumption/model/rentalobject-consumption-meter';
import { MeterService } from '../../../consumption/service/meter.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { standardCommand, Command } from '../../../core/model/command';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { LettingAdvertisment } from '../../../letting/model/letting-advertisment';
import { LettingAdvertismentParkingService } from '../../../letting/service/letting-advertisment-parking.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { rentalObjectFormCommand } from '../../model/const/command';
import { FormOfLease, FormOfLeaseToString, LeaseAgreementCategory, RentalObjectCategory } from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { Parking } from '../../model/parking';
import { RentApplication } from '../../model/rent-application';
import { CreateContractRentalObjectCommandHandler } from '../../service/command/create-contract-rental-object-command-handler.service';
import { RentalObjectFormCommandHandlerResolver } from '../../service/command/rental-object-form-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CompanyService } from '../../service/data/company.service';
import { ParkingService } from '../../service/data/parking.service';
import { PropertyService } from '../../service/data/property.service';
import { RentalObjectLogService } from '../../service/data/rental-object-log.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';
import { RentalObjectFormComponent } from '../base/rental-object.base';
import { LicenseService } from '../../../common/service/data/license.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';

const formControlName = {
  indvidualmeterelectricity: 'indvidualmeterelectricity',
};

@Component({
    selector: 'app-parking-form',
    templateUrl: './parking-form.component.html',
    styleUrls: ['./parking-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        CreateContractRentalObjectCommandHandler,
        RentalObjectFormCommandHandlerResolver,
        { provide: 'category', useValue: RentalObjectCategory.Parking },
        NavigationService,
        { provide: DataService, useExisting: ParkingService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ParkingFormComponent extends RentalObjectFormComponent<Parking> {
  protected override get category(): RentalObjectCategory {
    return RentalObjectCategory.Parking;
  }

  constructor(
    activatedRoute: ActivatedRoute,
    private _cdf: ColumnDefinitionFactory,
    companyService: CompanyService,
    private _dataService: ParkingService,
    dispathcher: CommandDispatcher,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    private cf: FormControlExFactory,
    handlerResolver: RentalObjectFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private _meterService: MeterService,
    propertyService: PropertyService,
    rentalObjectTypeService: RentalObjectTypeService,
    faultNotificationService: FaultNotificationService,
    columnDefinitionService: ColumnDefinitionService,
    logService: RentalObjectLogService,
    private readonly _userAccountService: UserAccountService,
    private readonly advertismentService: LettingAdvertismentParkingService,
    licenseService: LicenseService,
    private readonly _cdr: ChangeDetectorRef,
    dimensionLabelsService: DimensionLabelsService
  ) {
    super(
      fb,
      cf,
      companyService,
      propertyService,
      rentalObjectTypeService,
      faultNotificationService,
      cds,
      activatedRoute,
      dispathcher,
      handlerResolver,
      _dataService,
      messageService,
      navigation,
      columnDefinitionService,
      logService,
      licenseService,
      dimensionLabelsService
    );

    this.AddSubscription(
      this._userAccountService.current.subscribe({
        next: account => {
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', rentalObjectFormCommand.createContract);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
            this.showAdverts = true;
          }
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditParking)
          ) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.EditParking)) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            const addChangeCategory = () => {
              if (this.dataContext?.id) this.addMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
            };
            addChangeCategory();
            this.AddSubscription(this.dataContextInitialized$.subscribe(addChangeCategory));
          }
        },
      })
    );

    this.leaseAgreementColumnDefinition = [
      this._cdf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._cdf.createMoney('COMMON.YEAR_RENT', 'totalYearRent'),
      this._cdf.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'totalYearRentInclVat', ColumnVisibility.optional),
      this._cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'totalYearRentPerSqm', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', ColumnVisibility.optional),
      this._cdf.createMoney(
        'LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT',
        'chargedYearRentThisYearInclVat',
        ColumnVisibility.optional
      ),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];

    this.vacantPeriodsColumnDefinition = [
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_FROM', 'fromDate'),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_TO', 'toDate'),
      this._cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear'),
    ];

    this.rentApplicationsColumnDefinition = [
      this._cdf.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._cdf.create('TENANT.CONTACT_NUMBER', 'contactCustomerNumber', ColumnVisibility.optional),
      this._cdf.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._cdf.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._cdf.create('TENANT.EMAIL', 'contactEmail', ColumnVisibility.default),
      this._cdf.create('TENANT.PHONE', 'contactPhone', ColumnVisibility.default),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];

    this.advertismentsColumnDefinition = [
      this._cdf.createDate('LETTING.ADVERTISMENT.CREATED_AT', 'createdAt', ColumnVisibility.mandatory),
      this._cdf.createTranslation('LETTING.ADVERTISMENT.PLATFORM', 'platformName', ColumnVisibility.mandatory),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_FROM', 'publishFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_TO', 'publishTo', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_FROM', 'vacancyFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_TO', 'vacancyTo', ColumnVisibility.optional),
    ];
  }

  onRentalObjectDocumentsCountChanged(count: number) {
    this.rentalObjectDocumentCount = count;
    this._cdr.markForCheck();
  }

  leaseAgreementColumnDefinition: ColumnDefinition[];
  vacantPeriodsColumnDefinition: ColumnDefinition[];
  rentApplicationsColumnDefinition: ColumnDefinition[];
  allowedFormsOfLease: SelectionItem[] = [];
  advertismentsColumnDefinition: ColumnDefinition[];
  showAdverts: boolean = false;
  advertisments: LettingAdvertisment[];
  rentalObjectDocumentCount = 0;

  protected createModel(): Parking {
    return new Parking();
  }

  consumptionIsExpanded = false;
  consumptionExpanded(expanded: boolean): void {
    this.consumptionIsExpanded = true;
  }

  mediaTypeElectricity = MeterMediaType.Electricity;
  propertyMeters: Meter[];

  brfForm: FormGroup;
  changedAt: Date;

  linkDocument() {
    const cmd = new Command<Parking>(rentalObjectCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  get propertyId(): string {
    let control = this.form.controls['property'];
    return control ? control.value : null;
  }

  get showBrf(): boolean {
    let formOfLease = this.form.controls['formOfLease'].value;
    return formOfLease === FormOfLease.CooperativeHousing || formOfLease === FormOfLease.Ownership;
  }

  protected getFormGroup(): FormGroup {
    let group = super.getFormGroup();
    this.brfForm = this.fb.group({
      share: this.fc.create('COOPERATIVE_HOUSING.SHARE', this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.share : null),
      sharePercentage: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_PERCENT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.sharePercentage : null
      ),
      shareCompanySum: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_COMPANY_SUM',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.shareCompanySum : null
      ),
      contributionAmount: this.cf.create(
        'COOPERATIVE_HOUSING.CONTRIBUTION_AMOUNT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.contributionAmount : new Money(this.dataContext.currencyCode, null)
      ),
      baseAnnualFee: this.cf.create(
        'COOPERATIVE_HOUSING.BASE_ANNUAL_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.baseAnnualFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentFee: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentDate: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_DATE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentDate : null
      ),
      economicalPlanNumber: this.cf.create(
        'COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.economicalPlanNumber : null
      ),
    });

    group.addControl(
      'parkingNumber',
      this.cf.create('PARKING_FORM.PARKING_NUMBER_LABEL', this.dataContext.parkingNumber, [Validators.maxLength(10)])
    );
    group.addControl('parkingHasElectricity', this.cf.create('PARKING_FORM.HASELECTRICITY_LABEL', this.dataContext.parkingHasElectricity));
    group.addControl('isGarage', this.cf.create('PARKING_FORM.ISGARAGE_LABEL', this.dataContext.isGarage));
    group.addControl('parkingHasRoof', this.cf.create('PARKING_FORM.PARKINGHASROOF_LABEL', this.dataContext.parkingHasRoof));
    group.addControl('surface', this.cf.create('RENTAL_OBJECT_FORM.SURFACE_LABEL', this.dataContext.surface));
    group.addControl('formOfLease', this.cf.create('RENTAL_OBJECT.FORM_OF_LEASE', this.dataContext.formOfLease));
    group.addControl('brfSpecifics', this.brfForm);
    group.addControl(
      formControlName.indvidualmeterelectricity,
      this.cf.create('DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL', this.dataContext.indvidualMeterElectricity)
    );
    group.addControl(
      'imdMeterElectricity',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterElectricity,
        'CONSUMPTION.METER_NAME.ELECTRICITY',
        'DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL',
        this.dataContext.indvidualMeterElectricity,
        formControlName.indvidualmeterelectricity
      )
    );
    group.addControl('brfSpecifics', this.brfForm);

    return group;
  }

  private createMeterForm(
    form: FormGroup,
    meter: RentalObjectConsumptionMeter,
    nameLabel: string,
    enableLabel: string,
    enabled: boolean,
    enableField: string
  ) {
    let group = this.fb.group({
      id: this.cf.createHidden(meter ? meter.id : null),
      name: this.cf.create(nameLabel, meter ? meter.name : null),
      imdMeterModelId: this.cf.create('CONSUMPTION.METER_MODEL.NAME', meter ? meter.imdMeterModelId : null),
      imdMeterId: this.cf.create('CONSUMPTION.METER.METER', meter ? meter.imdMeterId : null),
      enabled: this.cf.create(enableLabel, enabled),
    });

    this.AddSubscription(
      group.controls['enabled'].valueChanges.subscribe(val => {
        form.controls[enableField].setValue(val);
      })
    );

    this.AddSubscription(
      group.controls['imdMeterId'].valueChanges.subscribe(val => {
        let meter = this.propertyMeters.find(m => m.id === val);
        if (meter) {
          group.controls['imdMeterModelId'].setValue(meter.imdMeterModelId);
        }
      })
    );

    return group;
  }

  private _getAgreementPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  get parkingElectricityPaymentOptions() {
    return this.showBrf ? this.parkingElectricityPaymentsBrf : this.parkingElectricityPayments;
  }

  protected prepareSave(): Parking {
    let model = super.prepareSave();
    model.changedAt = this.changedAt;
    if (!model.brfSpecifics.assignmentFee || !model.brfSpecifics.assignmentFee.amount)
      model.brfSpecifics.assignmentFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.baseAnnualFee || !model.brfSpecifics.baseAnnualFee.amount)
      model.brfSpecifics.baseAnnualFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.contributionAmount || !model.brfSpecifics.contributionAmount.amount)
      model.brfSpecifics.contributionAmount = new Money(model.currencyCode, 0);

    return model;
  }

  onRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getAgreementPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onApplicationRowClicked(e: { context: RentApplication; event: any }): void {
    this.navigation.navigate(['rent-application', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onAdvertismentRowClicked(e: { context: LettingAdvertisment; event: any }): void {
    this.navigation.navigateChild([this.dataContext.id, 'advertisments', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  override formSubmittedChange(response?: any) {
    this.changedAt = response;
    super.formSubmittedChange(response);
  }

  protected init(parking: Parking) {
    super.init(parking);
    this.changedAt = parking.changedAt;

    this.allowedFormsOfLease = parking.allowedFormsOfLease
      ? parking.allowedFormsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f))
      : [];

    this.form.controls['rentalObjectType'].valueChanges.subscribe(value => {
      if (!this.rentalObjectTypes) return;

      let rentalObjectType = this.rentalObjectTypes.find(rType => rType.value === value);
      if (rentalObjectType) {
        this.form.controls['isGarage'].setValue(rentalObjectType.isGarage);
        this.form.controls['parkingHasElectricity'].setValue(rentalObjectType.parkingHasElectricity);
        this.form.controls['parkingHasRoof'].setValue(rentalObjectType.parkingHasRoof);
      }
    });

    this.AddSubscription(
      this.form.controls['company'].valueChanges
        .pipe(switchMap(value => this._dataService.getValidFormsOfLease(value)))
        .subscribe(formsOfLease => {
          this.allowedFormsOfLease = formsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f));
          if (!this.allowedFormsOfLease.find(f => f.value === this.form.controls['formOfLease'].value)) {
            this.form.controls['formOfLease'].setValue(this.allowedFormsOfLease[0].value);
          }
        })
    );

    if (parking.propertyId) {
      this.AddSubscription(this._meterService.getPropertyMeters(parking.propertyId).subscribe(meters => (this.propertyMeters = meters)));
    }

    this.AddSubscription(
      this.form.controls['property'].valueChanges
        .pipe(switchMap(value => this._meterService.getPropertyMeters(value)))
        .subscribe(meters => {
          this.propertyMeters = meters;
        })
    );
  }

  protected override initializeSection(section: string): void {
    if (section === 'advertisments' && !this.isNew) {
      this.AddSubscription(
        this.advertismentService.getRentalObjectAdvertisments(this.dataContext.id).subscribe({
          next: result => (this.advertisments = result),
        })
      );
    }

    super.initializeSection(section);
  }
}
